import { PRODUCTS_URL } from '../constants';
import { apiSlice } from './apiSlice';

export const productApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getProducts: builder.query({
            query: ({ keyword, pageNumber, priceAssigned, sort }) => ({
                url: PRODUCTS_URL,
                params: {
                    keyword,
                    pageNumber,
                    priceAssigned,
                    sort,
                },
            }),
            keepUnusedDataFor: 5,
            providesTags: (result, error, { pageNumber }) =>
                result && result.products
                    ? [
                          ...result.products.map(({ _id }) => ({ type: 'Products', id: _id })),
                          { type: 'Products', id: `LIST-${pageNumber}` },
                      ]
                    : [{ type: 'Products', id: `LIST-${pageNumber}` }],
        }),
        getProductDetails: builder.query({
            query: (productId) => ({
                url: `${PRODUCTS_URL}/${productId}`,
            }),
            keepUnusedDataFor: 5,
            providesTags: (result, error, id) => [{ type: 'Products', id }],
        }),
        getProductDetailsByCtfId: builder.query({
            query: ({ ctfid }) => ({
                url: `${PRODUCTS_URL}/ctf/${ctfid}`,
            }),
            keepUnusedDataFor: 5,
            providesTags: (result, error, id) => [{ type: 'Products', id }],
        }),
        createProduct: builder.mutation({
            query: (product) => ({
                url: PRODUCTS_URL,
                method: 'POST',
                body: { ...product },
            }),
            invalidatesTags: [{ type: 'Products', id: 'LIST-1' }],
        }),
        updateProduct: builder.mutation({
            query: (product) => ({
                url: `${PRODUCTS_URL}/${product._id}`,
                method: 'PUT',
                body: { ...product },
            }),
            invalidatesTags: (result, error, product) => [{ type: 'Products', id: product._id }],
        }),
        deleteProduct: builder.mutation({
            query: ({ contificoId }) => ({
                url: `${PRODUCTS_URL}/${contificoId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { contificoId }) => [
                { type: 'Products', id: contificoId },
            ],
        }),
        createReview: builder.mutation({
            query: (data) => ({
                url: `${PRODUCTS_URL}/${data.productId}/reviews`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Products'],
        }),
        getTopProducts: builder.query({
            query: ({ priceAssigned }) => ({
                url: `${PRODUCTS_URL}/top`,
                params: { priceAssigned },
            }),
            keepUnusedDataFor: 5,
        }),
        getProductsByCategoryId: builder.query({
            query: ({ categoryId, pageNumber = 1, priceAssigned, sort }) => ({
                url: `${PRODUCTS_URL}/category/${categoryId}`, // 🛠 Pass the id inside the URL
                params: { pageNumber, priceAssigned, sort }, // 🛠 Only send pageNumber
            }),
            keepUnusedDataFor: 5,
            providesTags: (result, error, { pageNumber }) =>
                result && result.products
                    ? [
                          ...result.products.map(({ _id }) => ({ type: 'Products', id: _id })),
                          { type: 'Products', id: `LIST-${pageNumber}` },
                      ]
                    : [{ type: 'Products', id: `LIST-${pageNumber}` }],
        }),
        getProductsByBrandId: builder.query({
            query: ({ brandId, pageNumber = 1, priceAssigned, sort }) => ({
                url: `${PRODUCTS_URL}/brand/${brandId}`,
                params: { pageNumber, priceAssigned, sort },
            }),
            keepUnusedDataFor: 5,
            providesTags: (result, error, { pageNumber }) =>
                result && result.products
                    ? [
                          ...result.products.map(({ _id }) => ({ type: 'Products', id: _id })),
                          { type: 'Products', id: `LIST-BRAND-${pageNumber}` },
                      ]
                    : [{ type: 'Products', id: `LIST-BRAND-${pageNumber}` }],
        }),
        getDiscountedProducts: builder.query({
            query: ({ pageNumber = 1, priceAssigned, sort }) => ({
                url: `${PRODUCTS_URL}/discounted`,
                params: { pageNumber, priceAssigned, sort },
            }),
            keepUnusedDataFor: 5,
            providesTags: (result, error, { pageNumber }) =>
                result && result.products
                    ? [
                          ...result.products.map(({ _id }) => ({ type: 'Products', id: _id })),
                          { type: 'Products', id: `LIST-DISCOUNTED-${pageNumber}` },
                      ]
                    : [{ type: 'Products', id: `LIST-DISCOUNTED-${pageNumber}` }],
        }),
        getOutOfStockProducts: builder.query({
            query: ({ keyword, pageNumber = 1, priceAssigned, sort }) => ({
                url: `${PRODUCTS_URL}/out-of-stock`,
                params: { keyword, pageNumber, priceAssigned, sort },
            }),
            keepUnusedDataFor: 5,
            providesTags: (result, error, { pageNumber }) =>
                result && result.products
                    ? [
                          ...result.products.map(({ _id }) => ({ type: 'Products', id: _id })),
                          { type: 'Products', id: `LIST-OUT-OF-STOCK-${pageNumber}` },
                      ]
                    : [{ type: 'Products', id: `LIST-OUT-OF-STOCK-${pageNumber}` }],
        }),
        getNewCollectionExists: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/new_collection_exists`,
            }),
            keepUnusedDataFor: 5,
        }),
        getRelatedProducts: builder.query({
            query: ({ productId, priceAssigned }) => ({
                url: `${PRODUCTS_URL}/${productId}/related`,
                params: { priceAssigned },
            }),
            keepUnusedDataFor: 5,
        }),
        getSuggestions: builder.query({
            query: ({ keyword }) => ({
                url: `${PRODUCTS_URL}/suggestions`,
                params: { keyword },
            }),
            keepUnusedDataFor: 5,
        }),
        getProductsByCompany: builder.query({
            query: ({ ruc, pageNumber = 1, priceAssigned, sort, category }) => ({
                url: `${PRODUCTS_URL}/company/${ruc}`,
                params: { pageNumber, priceAssigned, sort, category },
            }),
            keepUnusedDataFor: 5,
            providesTags: (result, error, { pageNumber }) =>
                result && result.products
                    ? [
                          ...result.products.map(({ _id }) => ({ type: 'Products', id: _id })),
                          { type: 'Products', id: `LIST-${pageNumber}` },
                      ]
                    : [{ type: 'Products', id: `LIST-${pageNumber}` }],
        }),
    }),
});

export const {
    useGetProductsQuery,
    useGetProductDetailsQuery,
    useGetProductDetailsByCtfIdQuery,
    useLazyGetProductDetailsByCtfIdQuery,
    useCreateProductMutation,
    useUpdateProductMutation,
    useDeleteProductMutation,
    useCreateReviewMutation,
    useGetTopProductsQuery,
    useGetProductsByCategoryIdQuery,
    useGetProductsByBrandIdQuery,
    useGetDiscountedProductsQuery,
    useGetOutOfStockProductsQuery,
    useGetNewCollectionExistsQuery,
    useGetRelatedProductsQuery,
    useGetSuggestionsQuery,
    useGetProductsByCompanyQuery,
} = productApiSlice;